<template>
  <div>
    <div v-if="customerSelected" class="w-full flex items-start justify-between mb-4 flex-wrap gap-3">
      <TabHeaders :tabs="tabList" :selectedTab="activeBucket" @tab-selected="onBucketChange" />

      <div class="flex items-center gap-3 flex-wrap">
        <div class="flex items-center text-sm">
          <span class="mr-2 text-secondary font-semibold uppercase text-xs tracking-wide">Show due as</span>
          <div class="inline-flex border border-gray-300 rounded overflow-hidden">
            <button
                v-for="opt in dueModeOptions"
                :key="opt.key"
                type="button"
                class="px-3 py-1 text-xs font-semibold transition-colors duration-150"
                :class="dueMode === opt.key ? 'bg-orange-300 text-orange-900' : 'bg-white text-secondary hover:bg-gray-100'"
                @click="dueMode = opt.key"
            >{{ opt.label }}</button>
          </div>
        </div>

        <div class="flex" style="width: 340px;">
          <input
              type="text"
              class="rounded-l bge-input bge-input-spacing flex-1"
              :value="filters.search"
              placeholder="Search name, serial, category, agent…"
              @input="onSearch($event)"
          />
          <button
              type="button"
              class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300"
              aria-label="Search"
          >
            <Icon iconType="search" />
          </button>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="bg-white border border-gray-200 rounded p-10 text-center text-sm text-gray-500">Loading equipment…</div>
    <div v-else-if="!customerSelected" class="bg-white border border-gray-200 rounded p-10 text-center text-sm text-gray-500">Select a customer to see their equipment.</div>
    <div v-else-if="!items.length" class="bg-white border border-gray-200 rounded p-10 text-center text-sm text-gray-500">No equipment for this customer.</div>
    <DataTable
        v-else
        :value="visibleItems"
        :rowClass="rowClass"
        :rowHover="true"
        dataKey="equipment.equipment_id"
        class="planner-table"
    >
      <Column header="Name" :headerStyle="{ width: '200px' }">
        <template #body="slotProps">
          <div class="text-sm font-semibold text-secondary truncate" :title="slotProps.data.equipment.name || ''">
            {{ slotProps.data.equipment.name || '—' }}
          </div>
          <div class="text-xs text-gray-400 font-mono truncate">SN {{ slotProps.data.equipment.serial_number || '—' }}</div>
        </template>
      </Column>

      <Column header="Description" :headerStyle="{ width: '220px' }" :bodyStyle="{ maxWidth: '220px' }">
        <template #body="slotProps">
          <div class="text-sm text-secondary truncate" style="max-width: 220px" :title="slotProps.data.equipment.product_description || slotProps.data.equipment.description || ''">
            {{ slotProps.data.equipment.product_description || slotProps.data.equipment.description || '—' }}
          </div>
        </template>
      </Column>

      <Column header="Category" :headerStyle="{ width: '130px' }">
        <template #body="slotProps">
          <div class="text-sm text-secondary truncate" :title="slotProps.data.equipment.product_category || ''">
            {{ slotProps.data.equipment.product_category || '—' }}
          </div>
        </template>
      </Column>

      <Column header="Service Agent" :headerStyle="{ width: '150px' }">
        <template #body="slotProps">
          <div class="text-sm text-secondary truncate" :title="slotProps.data.equipment.service_agent_name || ''">
            {{ slotProps.data.equipment.service_agent_name || '—' }}
          </div>
        </template>
      </Column>

      <Column header="Service" :headerStyle="{ width: '96px' }">
        <template #body="slotProps">
          <DateCell
              :expiry="serviceExpiry(slotProps.data)"
              :info="serviceInfo(slotProps.data)"
              :due-mode="dueMode"
              kind="due"
          />
        </template>
      </Column>
      <Column header="Calibration" :headerStyle="{ width: '96px' }">
        <template #body="slotProps">
          <DateCell
              :expiry="calibrationExpiry(slotProps.data)"
              :info="calibrationInfo(slotProps.data)"
              :due-mode="dueMode"
              kind="due"
          />
        </template>
      </Column>
      <Column header="Warranty" :headerStyle="{ width: '96px' }">
        <template #body="slotProps">
          <DateCell :expiry="warrantyExpiry(slotProps.data)" :info="null" kind="plain" />
        </template>
      </Column>

      <Column header="Service" :headerStyle="{ width: '118px' }">
        <template #body="slotProps">
          <ActionCell
              category="service"
              :equipment="slotProps.data.equipment"
              :schedule-detail="slotProps.data.scheduleDetail"
              :current-schedule-id="currentScheduleId"
              :readonly="readonly"
              @toggle="onToggle(slotProps.data, $event)"
          />
        </template>
      </Column>
      <Column header="Calibration" :headerStyle="{ width: '118px' }">
        <template #body="slotProps">
          <ActionCell
              category="calibration"
              :equipment="slotProps.data.equipment"
              :schedule-detail="slotProps.data.scheduleDetail"
              :current-schedule-id="currentScheduleId"
              :readonly="readonly"
              @toggle="onToggle(slotProps.data, $event)"
          />
        </template>
      </Column>
      <Column header="Warranty" :headerStyle="{ width: '118px' }">
        <template #body="slotProps">
          <ActionCell
              category="warranty"
              :equipment="slotProps.data.equipment"
              :schedule-detail="slotProps.data.scheduleDetail"
              :current-schedule-id="currentScheduleId"
              :readonly="readonly"
              @toggle="onToggle(slotProps.data, $event)"
          />
        </template>
      </Column>
      <Column header="Repair" :headerStyle="{ width: '100px' }">
        <template #body="slotProps">
          <ActionCell
              category="repair"
              :equipment="slotProps.data.equipment"
              :schedule-detail="slotProps.data.scheduleDetail"
              :current-schedule-id="currentScheduleId"
              :readonly="readonly"
              @toggle="onToggle(slotProps.data, $event)"
          />
        </template>
      </Column>
      <Column header="Install" :headerStyle="{ width: '100px' }">
        <template #body="slotProps">
          <ActionCell
              category="install"
              :equipment="slotProps.data.equipment"
              :schedule-detail="slotProps.data.scheduleDetail"
              :current-schedule-id="currentScheduleId"
              :readonly="readonly"
              @toggle="onToggle(slotProps.data, $event)"
          />
        </template>
      </Column>

      <template #empty>
        No equipment matches these filters.
      </template>
    </DataTable>

    <div v-if="customerSelected && items.length" class="flex items-center justify-between mt-2 text-xs text-gray-500">
      <span>
        <span class="font-bold text-secondary">{{ visibleItems.length }}</span> of {{ items.length }} rows ·
        <span class="font-bold text-primary">{{ selectedCategoryCount }}</span> selections
      </span>
      <span>Tip: click any category cell to add it to this schedule</span>
    </div>
  </div>
</template>

<script>
import Icon from "@/components/Icon.vue";
import TabHeaders from "@/components/TabHeaders.vue";
import DateCell from "./DateCell.vue";
import ActionCell from "./ActionCell.vue";
import { CATEGORIES, categoryExpiryInfo, categoryPillState, rowBucket } from "@/utils/equipmentLocks";

export default {
  name: "EquipmentPlannerList",
  components: { Icon, TabHeaders, DateCell, ActionCell },
  props: {
    items: { type: Array, default: () => [] },
    visibleItems: { type: Array, default: () => [] },
    filters: { type: Object, required: true },
    currentScheduleId: { type: String, default: null },
    isLoading: { type: Boolean, default: false },
    customerSelected: { type: Boolean, default: false },
    bucketCounts: { type: Object, default: () => ({}) },
    highlightEquipmentId: { type: String, default: null },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      dueMode: 'date',
      dueModeOptions: [
        { key: 'date', label: 'Date' },
        { key: 'days', label: 'Due text' },
      ],
    };
  },
  computed: {
    activeBucket() {
      return this.filters.bucket || 'all';
    },
    selectedCategoryCount() {
      let n = 0;
      for (const item of this.items) {
        const sd = item && item.scheduleDetail;
        if (!sd) continue;
        for (const c of CATEGORIES) {
          if (sd[`is_${c.key}`]) n++;
        }
      }
      return n;
    },
    tabList() {
      const c = this.bucketCounts || {};
      return [
        { id: 'on_this_schedule', label: 'This Schedule',  stats: { count: c.on_this_schedule  || 0 } },
        { id: 'booked_elsewhere', label: 'Other Schedule', stats: { count: c.booked_elsewhere  || 0 } },
        { id: 'has_due_work',     label: 'Upcoming',       stats: { count: c.has_due_work      || 0 } },
        { id: 'follow_up',        label: 'Follow-up',      stats: { count: c.follow_up         || 0 } },
        { id: 'unscheduled',      label: 'Unscheduled',    stats: { count: c.unscheduled       || 0 } },
        { id: 'all',              label: 'All',            stats: { count: c.all               || 0 } },
      ];
    },
  },
  methods: {
    onSearch(event) {
      this.$emit('filter-change', { key: 'search', value: event.target.value });
    },
    onBucketChange(key) {
      this.$emit('filter-change', { key: 'bucket', value: key });
    },
    onToggle(rowData, payload) {
      const equipment_id = rowData && rowData.equipment && rowData.equipment.equipment_id;
      if (!equipment_id) return;
      this.$emit('toggle-category', {
        equipment_id,
        category: payload.category,
        value: payload.value,
      });
    },
    serviceExpiry(row) {
      const eq = row && row.equipment || {};
      return eq.latestServiceExpiry || eq.next_service || null;
    },
    calibrationExpiry(row) {
      const eq = row && row.equipment || {};
      return eq.latestCalibrationExpiry || eq.next_calibration || null;
    },
    warrantyExpiry(row) {
      const eq = row && row.equipment || {};
      return eq.latestWarrantyExpiry || eq.warranty_expiry || null;
    },
    serviceInfo(row) {
      return categoryExpiryInfo(row && row.equipment, 'service');
    },
    calibrationInfo(row) {
      return categoryExpiryInfo(row && row.equipment, 'calibration');
    },
    rowClass(row) {
      if (!row || !row.equipment) return '';
      const eq = row.equipment;
      const sd = row.scheduleDetail;
      const isHighlighted = !!(this.highlightEquipmentId && eq.equipment_id === this.highlightEquipmentId);
      const bucket = rowBucket(eq, sd, this.currentScheduleId);
      let hasOverdue = false;
      for (const cat of CATEGORIES) {
        if (!cat.hasDue) continue;
        const s = categoryPillState(eq, cat.key, this.currentScheduleId, sd);
        if (s !== 'due') continue;
        const info = categoryExpiryInfo(eq, cat.key);
        if (info && info.overdue) { hasOverdue = true; break; }
      }
      const classes = [];
      if (hasOverdue) classes.push('planner-row-overdue');
      else if (bucket === 'on_this_schedule') classes.push('planner-row-selected');
      if (isHighlighted) classes.push('planner-row-highlighted');
      return classes.join(' ');
    },
    scrollHighlightedIntoView() {
      if (!this.highlightEquipmentId) return;
      this.$nextTick(() => {
        const root = this.$el;
        if (!root || !root.querySelector) return;
        const rows = root.querySelectorAll('.p-datatable-tbody > tr');
        for (const tr of rows) {
          if (tr.classList.contains('planner-row-highlighted')) {
            if (typeof tr.scrollIntoView === 'function') {
              tr.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            break;
          }
        }
      });
    },
  },
  watch: {
    highlightEquipmentId() {
      this.scrollHighlightedIntoView();
    },
    visibleItems() {
      this.scrollHighlightedIntoView();
    },
  },
  mounted() {
    this.scrollHighlightedIntoView();
  },
};
</script>

<style scoped>
::v-deep .planner-table {
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  overflow: hidden;
}
::v-deep .planner-table .p-datatable-wrapper {
  border: none;
}
::v-deep .planner-table .p-datatable-thead > tr > th {
  background-color: #F8F9FB !important;
  color: #6B7280;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 10px 10px;
  border-top: none;
  border-bottom: 1px solid #E5E7EB;
}
::v-deep .planner-table .p-datatable-tbody > tr > td {
  padding: 8px 10px;
  font-size: 12.5px;
  border-top: none;
  border-bottom: 1px solid #F1F3F5;
}
::v-deep .planner-table .p-datatable-tbody > tr:last-child > td {
  border-bottom: none;
}
::v-deep .planner-table .p-datatable-tbody > tr.planner-row-overdue {
  background-color: #FFF7F5;
}
::v-deep .planner-table .p-datatable-tbody > tr.planner-row-overdue:hover {
  background-color: #FFF0EC !important;
}
::v-deep .planner-table .p-datatable-tbody > tr.planner-row-selected {
  background-color: #FFFBF4;
}
::v-deep .planner-table .p-datatable-tbody > tr.planner-row-selected:hover {
  background-color: #FFF5E6 !important;
}
::v-deep .planner-table .p-datatable-tbody > tr:hover {
  background-color: #FAFBFC;
}
::v-deep .planner-table .p-datatable-tbody > tr.planner-row-highlighted {
  box-shadow: inset 3px 0 0 0 #2563EB;
  background-color: #EFF6FF;
}
::v-deep .planner-table .p-datatable-tbody > tr.planner-row-highlighted:hover {
  background-color: #DBEAFE !important;
}
</style>
